// src/pages/Accounting.js
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import React, { useEffect, useState } from "react";
import { toJalaali } from "jalaali-js";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useAuth } from "../hooks/useAuth";
import "./AccountingPage.css";

// ----------------------
// مسیر API تک‌رستورانی
// ----------------------
const API_BASE =
  window.location.hostname === "localhost"
    ? "http://localhost/restaurant_api"
    : "https://menu-rtl.ir/restaurant_api";

// ----------------------
// تبدیل تاریخ
// ----------------------
function pad2(n) {
  return String(n).padStart(2, "0");
}

function toShamsiNumeric(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const j = toJalaali(d);
  return `${j.jy}/${pad2(j.jm)}/${pad2(j.jd)}`;
}

function toShamsiLong(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const j = toJalaali(d);

  const weekdays = [
    "یکشنبه",
    "دوشنبه",
    "سه‌شنبه",
    "چهارشنبه",
    "پنج‌شنبه",
    "جمعه",
    "شنبه",
  ];
  const months = [
    "فروردین",
    "اردیبهشت",
    "خرداد",
    "تیر",
    "مرداد",
    "شهریور",
    "مهر",
    "آبان",
    "آذر",
    "دی",
    "بهمن",
    "اسفند",
  ];

  return `${weekdays[d.getDay()]} - ${j.jd} ${months[j.jm - 1]} ${j.jy}`;
}

export default function Accounting() {
  const [orders, setOrders] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const { checkAuth } = useAuth();

  // ----------------------------
  // دریافت سفارش‌ها از سرور
  // ----------------------------
  const fetchPrinted = async () => {
    if (!checkAuth()) return;

    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/view_printed_orders.php`);

      const raw = await res.text();

      let json;
      try {
        json = JSON.parse(raw);
      } catch {
        // در صورت Warning از PHP → JSON واقعی را جدا کن
        const found = raw.match(/\{.*\}/s);
        if (found) json = JSON.parse(found[0]);
      }

      if (!json || !Array.isArray(json)) {
        setOrders([]);
        setFiltered([]);
        setError("داده‌های دریافتی معتبر نیستند.");
        return;
      }

      // ایجاد id یکتا برای ردیف‌هایی که id ندارند
      const finalList = json.map((o, idx) => ({
        ...o,
        id: o.id || `file_${idx}`,
      }));

      setOrders(finalList);
      setFiltered(finalList);
    } catch (e) {
      console.error("Fetch error:", e);
      setError("عدم ارتباط با سرور");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrinted();
  }, []);

  // ----------------------------
  // حذف سفارش
  // ----------------------------
  const handleDelete = async (orderId, filename) => {
    if (!checkAuth()) return;

    if (!window.confirm("آیا از حذف این سفارش مطمئن هستید؟")) return;

    setDeletingId(orderId);

    try {
      const res = await fetch(`${API_BASE}/delete_printed_order.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: orderId, filename }),
      });

      const raw = await res.text();
      let json;

      try {
        json = JSON.parse(raw);
      } catch {
        const found = raw.match(/\{.*\}/s);
        if (found) json = JSON.parse(found[0]);
      }

      if (json?.success) {
        setOrders((p) => p.filter((o) => o.id !== orderId));
        setFiltered((p) => p.filter((o) => o.id !== orderId));
        alert("سفارش حذف شد.");
      } else {
        alert("خطا در حذف سفارش.");
      }
    } catch (e) {
      console.error(e);
      alert("خطای ارتباط با سرور.");
    } finally {
      setDeletingId(null);
    }
  };

  // ----------------------------
  // فیلتر تاریخ
  // ----------------------------
  const convertPickerDate = (inp) => {
    if (!inp) return null;
    if (inp.toDate) return inp.toDate();

    if (typeof inp === "string") {
      const [y, m, d] = inp.split("/").map(Number);
      return new Date(y, m - 1, d);
    }
    return null;
  };

  const handleFilter = () => {
    if (!checkAuth()) return;

    const s = convertPickerDate(startDate);
    const e = convertPickerDate(endDate);

    if (!s || !e) return alert("بازه تاریخ را انتخاب کنید.");

    e.setHours(23, 59, 59);

    setFiltered(
      orders.filter((o) => {
        const t = new Date(o.time);
        return t >= s && t <= e;
      })
    );
  };

  const clearFilter = () => {
    if (!checkAuth()) return;

    setStartDate("");
    setEndDate("");
    setFiltered(orders);
  };

  const totalAmount = filtered.reduce(
    (t, o) => t + (Number(o.total_price) || 0),
    0
  );

  const handleExportExcel = () => {
    if (!checkAuth()) return;

    const rows = filtered.map((o) => {
      const t = new Date(o.time);
      return {
        میز: o.table_number,
        تاریخ: toShamsiNumeric(o.time),
        روز: toShamsiLong(o.time),
        زمان: t.toLocaleTimeString("fa-IR"),
        مبلغ: Number(o.total_price),
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "report");

    const out = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    saveAs(new Blob([out]), `report_${Date.now()}.xlsx`);
  };

  return (
    <div className="accounting-container">
      <div className="header-actions">
        <h1>گزارش حسابداری</h1>

        <div className="btn-group">
          <button className="btn-glass-secondary" onClick={fetchPrinted}>
            بروزرسانی
          </button>
          <button className="btn-glass-secondary" onClick={clearFilter}>
            پاکسازی
          </button>
          <button className="btn-glass-success" onClick={handleExportExcel}>
            اکسل‌گیری
          </button>
        </div>
      </div>

      <div className="filter-box">
        <label>از</label>
        <DatePicker
          value={startDate}
          onChange={setStartDate}
          calendar={persian}
          locale={persian_fa}
          format="YYYY/MM/DD"
        />

        <label>تا</label>
        <DatePicker
          value={endDate}
          onChange={setEndDate}
          calendar={persian}
          locale={persian_fa}
          format="YYYY/MM/DD"
        />

        <button className="btn-glass-primary" onClick={handleFilter}>
          فیلتر
        </button>
      </div>

      {loading ? (
        <div className="loading">در حال بارگذاری...</div>
      ) : error ? (
        <div className="error-box">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">هیچ سفارشی یافت نشد.</div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>میز</th>
                <th>تاریخ</th>
                <th>روز</th>
                <th>زمان</th>
                <th>مبلغ</th>
                <th>عملیات</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((o, i) => {
                const t = new Date(o.time);
                return (
                  <tr key={o.id}>
                    <td>{o.table_number}</td>
                    <td>{toShamsiNumeric(o.time)}</td>
                    <td>{toShamsiLong(o.time)}</td>
                    <td>{t.toLocaleTimeString("fa-IR")}</td>
                    <td>
                      {Number(o.total_price).toLocaleString("fa-IR")} تومان
                    </td>
                    <td>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(o.id, o.filename)}
                        disabled={deletingId === o.id}
                      >
                        {deletingId === o.id ? "⏳" : "🗑️"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>

            <tfoot>
              <tr>
                <td colSpan="4">جمع کل</td>
                <td>{totalAmount.toLocaleString("fa-IR")} تومان</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
